import React from 'react';
import Button from '../components/UI/Button';
import { getProductById } from '../data/products';
import './WoprPage.css';
import './ProductHero.css';

const MandatumPage = () => {
  const product = getProductById('mandatum');

  if (!product) {
    return <div>Product not found</div>;
  }

  return (
    <div className="wopr-page">
      <section className="product-hero product-hero-mandatum">
        <div className="product-hero-content">
          <h1 className="product-hero-title"><span>Mandatum</span></h1>
          <p className="product-hero-tagline">{product.tagline}</p>
        </div>
      </section>

      <section className="product-overview-section">
        <div className="container">
          <div className="product-overview">
            <div className="product-overview-image">
              <img src={product.listImage} alt={product.name} />
            </div>
            <div className="product-overview-content">
              <h2 className="section-title">About {product.name}</h2>
              <p className="product-overview-desc">{product.description}</p>
              <p className="product-overview-desc">
                Manage your prompts visually while writing custom code that runs before and after
                your LLM calls to fetch data, transform inputs, validate outputs, and route responses.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="features-section bg-light">
        <div className="container">
          <h2 className="section-title text-center">Features</h2>
          <p className="section-subtitle text-center" style={{ margin: '0 auto 3rem' }}>
            Everything you need to manage and deploy AI prompts at scale.
          </p>
          <div className="features-grid">
            {product.features.map((feature, index) => (
              <div key={index} className="feature-item">
                <span className="feature-check">✓</span>
                <span className="feature-text">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="product-detail-section">
        <div className="container">
          <h2 className="section-title text-center">How It Works</h2>
          <p className="section-subtitle text-center" style={{ margin: '0 auto 3rem', maxWidth: '700px' }}>
            Mandatum follows a streamlined workflow: Create, Test, Deploy, and Monitor your AI prompts with full version control.
          </p>
          <div className="detail-grid">
            <div className="detail-card">
              <h3>Prompts</h3>
              <p>
                Templates with dynamic variables using {'{variable_name}'} syntax. Every save creates a new version,
                giving you complete history and instant rollback.
              </p>
              <p>
                Switch between OpenAI, Anthropic, and Google models without changing your code.
              </p>
            </div>
            <div className="detail-card">
              <h3>Custom Integrations</h3>
              <p>
                Write Python or TypeScript code that runs alongside your prompts:
              </p>
              <ul className="detail-list">
                <li><strong>Pre-processors</strong> — fetch data, validate inputs, inject context</li>
                <li><strong>Post-processors</strong> — parse responses, validate data, route outputs</li>
              </ul>
            </div>
            <div className="detail-card">
              <h3>The Playground</h3>
              <p>
                Test prompts with real or mock data instantly. Compare models side-by-side and see immediate results
                including your custom code execution.
              </p>
            </div>
            <div className="detail-card">
              <h3>Analytics & Monitoring</h3>
              <p>
                Every LLM call is logged with full context. Track costs per prompt, user, or team.
                Monitor latency, error rates, and token usage in real-time.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="product-detail-section bg-light">
        <div className="container">
          <h2 className="section-title text-center">Getting Started</h2>
          <p className="section-subtitle text-center" style={{ margin: '0 auto 3rem', maxWidth: '600px' }}>
            Go from sign-up to production in minutes.
          </p>
          <div className="steps-grid">
            <div className="step-item">
              <span className="step-number">1</span>
              <div className="step-content">
                <h4>Sign Up</h4>
                <p>Create an account with Google OAuth and join or create an organization.</p>
              </div>
            </div>
            <div className="step-item">
              <span className="step-number">2</span>
              <div className="step-content">
                <h4>Create Your First Prompt</h4>
                <p>Select a provider and model, write your template with variables like {'{user_name}'}.</p>
              </div>
            </div>
            <div className="step-item">
              <span className="step-number">3</span>
              <div className="step-content">
                <h4>Test in the Playground</h4>
                <p>Enter test values and run instantly. Compare results across different models.</p>
              </div>
            </div>
            <div className="step-item">
              <span className="step-number">4</span>
              <div className="step-content">
                <h4>Get Your API Key</h4>
                <p>Generate an API key in Settings to call prompts from your application.</p>
              </div>
            </div>
            <div className="step-item">
              <span className="step-number">5</span>
              <div className="step-content">
                <h4>Monitor & Iterate</h4>
                <p>Track requests, costs, and performance. Iterate with full version history.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="product-detail-section">
        <div className="container">
          <div className="doc-card">
            <p>
              For detailed guides, API reference, and code examples, visit the full documentation.
            </p>
            <Button href="https://mandatum-documentation.netlify.app/" variant="primary" target="_blank">
              View Documentation
            </Button>
          </div>
        </div>
      </section>

      <section className="get-started-section">
        <div className="container">
          <div className="get-started-grid">
            <div className="get-started-download">
              <h2 className="section-title">Try {product.name}</h2>
              <p className="get-started-subtitle">Live and in alpha. Fully functional and ready for testing.</p>
              <div className="download-buttons">
                <Button href={product.siteUrl} variant="primary" size="large">
                  Visit Website
                </Button>
              </div>
              <div className="requirements-inline">
                <span className="requirements-label">Requirements:</span>
                {product.requirements.map((req, index) => (
                  <span key={index} className="requirement-tag">{req}</span>
                ))}
              </div>
            </div>
            <div className="get-started-support">
              <h2 className="section-title">Need Help?</h2>
              <p className="get-started-subtitle">
                If you have questions or need support, we're here to help.
              </p>
              <Button to="/contact" variant="outline-dark">
                Contact Support
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MandatumPage;
